export const HEIC_PROCESSING_ERROR =
  'We couldn’t process this iPhone photo. Try choosing another photo or save it as JPG and upload again.';

const HEIC_EXTENSIONS = ['.heic', '.heif'];
const JPEG_EXTENSIONS = ['.jpg', '.jpeg'];
const PNG_EXTENSIONS = ['.png'];
const MAX_UPLOAD_IMAGE_BYTES = 4 * 1024 * 1024;
const MAX_UPLOAD_IMAGE_EDGE = 1800;

const getExtension = file => {
  const name = file?.name || '';
  const lastDot = name.lastIndexOf('.');
  return lastDot >= 0 ? name.slice(lastDot).toLowerCase() : '';
};

export const isHeicFile = file => {
  const type = file?.type?.toLowerCase() || '';
  return type === 'image/heic' || type === 'image/heif' || HEIC_EXTENSIONS.includes(getExtension(file));
};

export const isSupportedListingImage = file => {
  const type = file?.type?.toLowerCase() || '';
  const extension = getExtension(file);
  return (
    type === 'image/jpeg' ||
    type === 'image/png' ||
    JPEG_EXTENSIONS.includes(extension) ||
    PNG_EXTENSIONS.includes(extension) ||
    isHeicFile(file)
  );
};

const toFile = (blob, file) => {
  const baseName = (file?.name || 'listing-image').replace(/\.[^.]+$/, '');
  return new File([blob], `${baseName}.jpg`, { type: 'image/jpeg', lastModified: Date.now() });
};

const canResizeInBrowser = () =>
  typeof document !== 'undefined' &&
  typeof Image !== 'undefined' &&
  typeof URL !== 'undefined' &&
  typeof URL.createObjectURL === 'function';

const imageSize = file =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      URL.revokeObjectURL(url);
      resolve({ width: image.naturalWidth || image.width, height: image.naturalHeight || image.height });
    };
    image.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Unable to read image dimensions.'));
    };
    image.src = url;
  });

const resizeImage = async file => {
  if (!canResizeInBrowser() || file.size <= MAX_UPLOAD_IMAGE_BYTES) return file;

  const { width, height } = await imageSize(file);
  const largestEdge = Math.max(width, height);
  if (!largestEdge || largestEdge <= MAX_UPLOAD_IMAGE_EDGE) return file;

  const scale = MAX_UPLOAD_IMAGE_EDGE / largestEdge;
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(width * scale);
  canvas.height = Math.round(height * scale);
  const context = canvas.getContext('2d');
  if (!context) return file;

  const url = URL.createObjectURL(file);
  const image = await new Promise((resolve, reject) => {
    const element = new Image();
    element.onload = () => resolve(element);
    element.onerror = () => reject(new Error('Unable to resize image.'));
    element.src = url;
  });

  try {
    context.drawImage(image, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/jpeg', 0.86));
    return blob ? toFile(blob, file) : file;
  } finally {
    URL.revokeObjectURL(url);
  }
};

export const prepareListingImage = async file => {
  if (!isSupportedListingImage(file)) {
    throw new Error('Please choose a JPG, PNG, HEIC, or HEIF image.');
  }

  if (!isHeicFile(file)) {
    try {
      return await resizeImage(file);
    } catch (error) {
      return file;
    }
  }

  try {
    const heic2anyModule = await import('heic2any');
    const heic2any = heic2anyModule.default || heic2anyModule;
    const converted = await heic2any({ blob: file, toType: 'image/jpeg', quality: 0.88 });
    const blob = Array.isArray(converted) ? converted[0] : converted;
    const convertedFile = toFile(blob, file);
    try {
      return await resizeImage(convertedFile);
    } catch (error) {
      return convertedFile;
    }
  } catch (error) {
    throw new Error(HEIC_PROCESSING_ERROR);
  }
};

export const processListingImageBatch = async (files, uploadFile) => {
  const uploaded = [];
  const errors = [];

  for (const [index, selectedFile] of files.entries()) {
    try {
      const file = await prepareListingImage(selectedFile);
      await uploadFile(file, index);
      uploaded.push(file);
    } catch (error) {
      errors.push({ file: selectedFile, error: error?.message || HEIC_PROCESSING_ERROR });
    }
  }

  return { uploaded, errors };
};
